/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {useContext, useEffect, useState} from 'react';
import {ExdsText} from '@exds/core';
import {ExdsIconCheckmark, ExdsIconForward} from '@exds/icons';
import './FinishSteps.scss';
import {STEP_PERFORMED} from "../../constants/constants";
import AppContext from "../../AppContext";

export default function FinishSteps({stepsCompleted}) {
  const {isSoftTokenEnabledContext, isSmartLogonEnabledContext} = useContext(AppContext);
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showCreateSecurityQuestions, setShowCreateSecurityQuestions] = useState(false);
  const [showRSASetup, setShowRSASetup] = useState(false);
  const [showSLOSetup, setShowSLOSetup] = useState(false);
  const isSoftTokenEnabledBool = isSoftTokenEnabledContext.at(0) === "true";
  const isSmartLogonEnabledBool = isSmartLogonEnabledContext.at(0) === "true";

  const startTimeout = 400;
  const interval = 100;

  useEffect(() => {
    const showCreatePasswordTimeout = setTimeout(() => {
      setShowCreatePassword(true);
    }, startTimeout);

    const showCreateSecurityQuestionsTimeout = setTimeout(() => {
      setShowCreateSecurityQuestions(true);
    }, startTimeout + interval);

    const showRSASetupTimeout = setTimeout(() => {
      setShowRSASetup(isSoftTokenEnabledBool);
    }, startTimeout + (interval * 2));

    const showSLOSetupTimeout = setTimeout(() => {
      setShowSLOSetup(isSmartLogonEnabledBool);
    }, startTimeout + (interval * 3));

    return () => {
      clearTimeout(showCreatePasswordTimeout);
      clearTimeout(showCreateSecurityQuestionsTimeout);
      if (isSoftTokenEnabledBool)
        clearTimeout(showRSASetupTimeout);
      if (isSmartLogonEnabledBool)
        clearTimeout(showSLOSetupTimeout);
    };
  });

  const iconCheck = (
    <ExdsIconCheckmark className="icon completed" aria-hidden color="inverse" size="medium"
      data-testid="icon-completed" />
  );
  const iconFwd = (
    <ExdsIconForward className="icon skipped" aria-hidden color="inverse" size="medium" data-testid="icon-skipped" />
  );
  const steps = {};
  if (isSoftTokenEnabledBool) {
    steps.rsa = {
      text: 'RSA Authenticator Setup',
      textSkipped: 'Set up RSA Authenticator',
      alt: 'step-3-rsa-authenticator',
      onClickParam: 'rsaInfo'
    };
  }
  if (isSmartLogonEnabledBool) {
    steps.smartLogOn = {
      text: 'Smart Log On Setup',
      textSkipped: 'Set up Smart Log On',
      alt: 'step-4-smart-log-on',
      onClickParam: 'smartLogOn'
    };
  }


  function getComponent(step, stepStatus) {
    const completed = stepStatus === STEP_PERFORMED;
    const icon = completed ? iconCheck : iconFwd;
    let comp;
    if (completed) {
      comp = (
        <ExdsText primitive="span" textStyle="bodyLarge">{step.text}</ExdsText>
      );
    } else {
      comp = (<><ExdsText className="link-button skipped-link">{step.textSkipped}</ExdsText>
          <ExdsText className="skipped-link-description" decoration="bold"> (skipped)</ExdsText> </>
      );
    }
    return (
      <p>
        {icon}{comp}
      </p>
    );
  }

  return (
    <div>
      <p className={`finish-subtext ${showCreatePassword ? 'fade-in' : ''}`}>
        {iconCheck}
        <ExdsText primitive="span" textStyle="bodyLarge">Create Log On Password</ExdsText>
      </p>
      <p className={`finish-subtext ${showCreateSecurityQuestions ? 'fade-in' : ''}`}>
        {iconCheck}
        <ExdsText primitive="span" textStyle="bodyLarge">Create Security Questions</ExdsText>
      </p>
      {(isSoftTokenEnabledBool || isSmartLogonEnabledBool) && (
        <>
          {isSoftTokenEnabledBool && (
            <p className={`finish-subtext ${showRSASetup ? 'fade-in' : ''}`}>
              {getComponent(steps.rsa, stepsCompleted?.rsaSetup)}
            </p>
          )}
          {isSmartLogonEnabledBool && (
            <p className={`finish-subtext ${showSLOSetup ? 'fade-in' : ''}`}>
              {getComponent(steps.smartLogOn, stepsCompleted?.sloSetup)}
            </p>
          )}
        </>
      )}
    </div>
  );
}
